import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { DetailsContent, DetailsHeader, DetailsPage } from "components/app_layout/DetailsLayout";
import { FilterableDocuments } from "components/documents/FilterableDocuments";
import { useSavedViews } from "components/documents/useDocumentsPageState";
import {
  areFiltersEqual,
  DEFAULT_DOCUMENT_FILTERS,
  DocumentFilters,
  expandDocumentCategories,
} from "components/documents/utils";
import { PaywallBanner } from "components/paywall/PaywallBanner";
import SearchActions from "components/saved_searches/SearchActions";
import { BULK_BUYING_SIGNALS, useVariableValue } from "lib/featureFlags";
import { useLogSearch } from "lib/hooks/api/userActivity/useLogSearch";
import { CurrentUserData, useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { TrackingProvider } from "lib/tracking";
import { LoggedEntityType } from "lib/types/graphQLEnums";
import { SavedDocumentView } from "lib/types/savedViews";

import css from "./DocumentsPage.module.scss";

function convertDocumentFiltersToSavedViewFilters(
  filters: DocumentFilters,
  user: CurrentUserData | undefined,
): SavedDocumentView["view"]["filterSettings"] {
  // Create a copy of filters without the sort field
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { sort, ...filtersWithoutSort } = filters;

  return {
    ...filtersWithoutSort,
    categories: expandDocumentCategories(filters.categories, user),
    publishDate: filters.publishDate?.filter ? { filter: filters.publishDate.filter } : undefined,
  };
}

export function DocumentsPage() {
  const { authorised: hasDocuments } = useCheckSubscription("DOCUMENTS");
  const { data: currentUser } = useCurrentUser();
  const isBulkBuyingSignalsEnabled = useVariableValue(BULK_BUYING_SIGNALS, false);

  const { filters, selectedView, filtersHaveChanged, setSelectedView, showNoView } =
    useSavedViews();
  useLogSearch(LoggedEntityType.BuyerDocument, hasDocuments ? filters : undefined);

  const viewStatus: { status: "unsaved" | "saved" | "standard"; message: string } =
    filtersHaveChanged()
      ? { status: "unsaved", message: "Unsaved" }
      : { status: "saved", message: "Saved" };

  return (
    <DetailsPage>
      <DetailsHeader>
        <Flex gap={8} justifyContent="space-between" alignItems="center">
          <Flex gap={8} alignItems="center">
            <Text h1>
              {selectedView?.name
                ? selectedView.name
                : showNoView && !filtersHaveChanged()
                  ? "Find documents"
                  : "Documents"}
            </Text>
            {(!showNoView || filtersHaveChanged()) && (
              <ViewStatus description status={viewStatus.status}>
                {viewStatus.message}
              </ViewStatus>
            )}
          </Flex>
          <SearchActions
            dataType="document"
            selectedView={selectedView}
            setSelectedView={setSelectedView}
            currentView={{
              areSettingsEqualToDefault: areFiltersEqual(filters, DEFAULT_DOCUMENT_FILTERS),
              haveSettingsChanged: filtersHaveChanged(),
              view: {
                sortOrder: {
                  field: filters.sort.field,
                  order: filters.sort.order,
                },
                tableSettings: {
                  columns: [
                    { field: "title" },
                    { field: "buyers" },
                    { field: "type" },
                    { field: "publishedAt" },
                  ],
                },
                filterSettings: convertDocumentFiltersToSavedViewFilters(filters, currentUser),
              } satisfies SavedDocumentView["view"],
            }}
          />
        </Flex>
      </DetailsHeader>
      <TrackingProvider data={{ "Page source": "Document search" }}>
        {!hasDocuments && <PaywallBanner featureType="DOCUMENTS" />}
        <DetailsContent className={css.content}>
          <FilterableDocuments
            defaultFilters={{ ...DEFAULT_DOCUMENT_FILTERS }}
            showBanner={true}
            showBuyingSignalsTab={isBulkBuyingSignalsEnabled}
          />
        </DetailsContent>
      </TrackingProvider>
    </DetailsPage>
  );
}

const ViewStatus = styled(Text)<{ status: "saved" | "unsaved" | "standard" }>(
  ({ theme, status }) => ({
    padding: "2px 8px",
    ...(status === "saved" && {
      background: theme.colors.sysPrimarySubtle,
      color: theme.colors.sysTextDefault,
    }),
    ...(status === "unsaved" && {
      background: theme.colors.sysWarningSubtle,
      color: "#7C4F07",
    }),
    ...(status === "standard" && {
      background: theme.colors.sysBackgroundAlternative,
      color: theme.colors.sysTextSecondary,
    }),
  }),
);
